import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft, Save, FileSignature, ShieldCheck, Lock, Loader2, AlertTriangle,
    ScanLine, Radiation, Baby, Layers, Images, ImageOff, ExternalLink, X,
    Clock, Stethoscope, History, Pencil, CheckCircle2, RefreshCw, FileText,
    ChevronRight, ClipboardList, Sparkles,
} from 'lucide-react';
import DictationPanel from '../../components/ai/DictationPanel';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
import {
    getRadiologyWorklist, getStudy, getTemplates,
    draftReport, signReport, amendReport,
} from '../../redux/radiologySlice';

const MySwal = withReactContent(Swal);

// THE READING ROOM. One study, the pictures it produced, and the two acts that
// turn them into a document a clinician may act on.
//
// THE TWO-PERSON RULE IS THE ENTIRE SHAPE OF THIS SCREEN, exactly as it is on
// LabResultEntry — and in radiology the split is even sharper, because the two
// halves are usually two different jobs in two different rooms. A RADIOGRAPHER
// makes the pictures (radiology.study.perform) and never appears on this screen.
// A RADIOLOGIST writes the interpretation (radiology.report.draft) and then, as a
// SEPARATE and deliberate act, signs it (radiology.report.sign).
//
//   study:   ordered -> scheduled -> arrived -> performed -> reported -> signed -> released
//   report:                                     draft -> signed -> released -> amended
//
// Drafting RECORDS an opinion. It publishes nothing and commits nobody: a draft is
// the radiologist thinking on paper, and it may be rewritten as many times as they
// like (the server allows draft -> draft for precisely that reason). Signing is a
// different claim entirely — that a named, REGISTERED person will answer for this
// interpretation in the question every radiology report eventually faces: "was the
// fracture visible on the first film, and who said it was not?". So the two are
// separated here by a visible wall — different pane, different colour, different
// weight, and a confirmation the signer has to read — because a Sign button that
// sits beside Save and looks like Save will eventually be pressed by the same
// finger on the way past.
//
// WHY SIGNING IS REFUSED WHILE ANYTHING IS UNSAVED.
// A signature is applied to what is STORED, never to what is on the screen. A
// radiologist who types "no fracture", then presses Sign, would otherwise be
// putting their registration number to the previous sentence still sitting in the
// database. So an unsaved edit shuts the sign pane and says why, in those words.
//
// WHY A SIGNATORY WITHOUT A REGISTRATION NUMBER IS REFUSED.
// A radiology report carries the registration number of whoever signed it. The
// server refuses with SIGNATORY_NOT_REGISTERED when the account carries no
// Doctor.licenseNumber and no Staff.registrationNo, and this screen says so in a
// sentence somebody can act on ("an administrator must add it to your record")
// rather than showing a raw code. It is a state the radiologist cannot fix by
// typing differently, and discovering it after a shift of reporting costs the
// department a turnaround it cannot get back.
//
// WHY A REPORT CANNOT BE SIGNED BEFORE ITS STUDY IS PERFORMED.
// Signing an interpretation of images that were never acquired produces a
// fabricated clinical document, and it is an easy accident on a screen opened from
// a queue that also lists tomorrow's bookings. imagingService.assertSignable is
// the authority; STUDY_REPORTABLE_STATUSES below mirrors it so the button is shut
// before the click rather than after it, and the ORDER of the checks mirrors it
// too — the study is tested before the signatory, so a radiologist who opened the
// wrong study is told that, and not told about their registration number instead.
//
// AN AMENDMENT IS A NEW VERSION AND NEVER AN EDIT.
// Once a report is signed its text is immutable, whether or not it has been handed
// over: the signature is what makes it so. Correcting it creates a NEW row
// carrying amendedFromReportId, the superseded one keeps its own text and moves to
// 'amended', and the correction is a DRAFT that has to be signed like any other
// report. The screen says all three of those things out loud while it is being
// written, because a radiologist who believes they are editing will write a
// sentence that only makes sense as a replacement ("as above, corrected") and it
// will be read beside the original.
//
// TEMPLATES ARE OFFERED AND NEVER APPLIED BY THEMSELVES.
// Typing a normal chest X-ray from scratch thirty times a day is how reports start
// being copied between patients, so templates exist. But a template that loaded
// itself into an empty report is how a radiologist signs "no abnormality" over an
// abnormal film without ever having typed the words, and a template dropped over
// half-written text is how ANOTHER patient's findings end up in this report. So
// applying one is always a deliberate pick, and overwriting anything already typed
// asks first and names the boxes it would replace.
//
// THE PC-PNDT SENTENCE IS NOT TYPED INTO THE FINDINGS, EVER.
// A report on an obstetric or pelvic ultrasound must carry "The sex of the foetus
// has not been detected or disclosed". It is a statutory declaration, so it is
// added by the PDF renderer from one explicit flag the server decides from the
// procedure and the patient — see the header of services/pdf/pdfkitRenderer.js,
// which refuses to infer it. This screen QUOTES it so the radiologist knows what
// their signature will carry, and deliberately does not put it in an editable box:
// text in a box can be deleted, and text in a box that the renderer also adds
// prints the declaration twice.
//
// THE IMAGES ARE SHOWN BECAUSE A REPORT WITHOUT THEM IS DICTATION FROM MEMORY.
// They arrive as presigned URLs that expire, so a link that has gone stale is
// reported as such with a reload beside it rather than left as a broken frame —
// a broken frame reads as "there are no images", which is a different clinical
// fact. `transferComplete` is the study's own claim that everything the console
// said it sent actually landed: reporting a study whose transfer broke halfway is
// reading a partial series, and it is called out before the signature, not after.
//
// ---------------------------------------------------------------------------
// THE API THIS SCREEN CONSUMES lives in redux/radiologySlice.js and no URL is
// written in this file — one caller per endpoint is what stops two screens
// drifting into two spellings of the same path.
//
//   getRadiologyWorklist({ queue, search, tenantId })   the picker, and the SAME
//       thunk the worklist screen uses — one name for one endpoint. `to_report` is
//       everything performed and not yet interpreted, `to_sign` everything
//       drafted and waiting for a signature. Both are queue ids the backend owns
//       (radiologyWorkflowController QUEUES), named for the ACT that is owed.
//
//   getStudy({ studyId, tenantId })            one study, whole:
//       study:            { studyId, accessionNo, status, nextStatuses, modality,
//                           procedureName, scheduledAt, arrivedAt, performedAt,
//                           performedBy, performedByName?, contrastUsed,
//                           contrastAgent, technique, dicomStudyUid, imageCount,
//                           remarks }
//       images:           [{ url }]   presigned, expiring, key never returned
//       imagesHeld:       how many objects are actually recorded
//       transferComplete: imageCount <= imagesHeld
//       patient:          { patientId, name, age, gender, dob }
//       procedure:        { procedureId, code, name, modality, bodyPart,
//                           preparation, radiationDose, requiresContrast, isPndt }
//       equipment:        { equipmentId, name, room, status, aerbRegNo,
//                           aerbExpiry, pndtRegNo, pndtExpiry }
//       equipmentProblem: null when the machine may lawfully be used
//       order:            { orderId, priority, orderedAt, clinicalNotes,
//                           doctorName, referrerRegNo }
//       pndt:             { applies, cleared, form, history }
//       reports:          [{ reportId, reportNo, status, version, clinicalHistory,
//                           technique, findings, impression, recommendation,
//                           draftedBy, draftedAt, signedByName, signedByRegNo,
//                           signedAt, releasedAt, amendedFromReportId,
//                           amendReason, nextStatuses }]  newest first
//       viewer?:          { name, registrationNo } when the backend supplies it —
//                         see signerIdentity for why its ABSENCE must not block.
//
//   getTemplates({ procedureId, modality, tenantId })
//       [{ templateId, name, technique, findings, impression, isDefault }]
//
//   draftReport({ studyId, clinicalHistory, technique, findings, impression,
//                 recommendation, tenantId })   creates the draft or rewrites it
//   signReport({ reportId, tenantId })          the signature
//   amendReport({ reportId, amendReason, ...narrative, tenantId })
//       supersedes `reportId` and returns the NEW draft
//
// Refusals this screen branches on rather than displays: SIGNATORY_NOT_REGISTERED,
// SIGNATORY_UNKNOWN, STUDY_NOT_PERFORMED, ILLEGAL_REPORT_TRANSITION.
// ---------------------------------------------------------------------------

/**
 * The five narrative blocks of a radiology report, in the order they are read.
 *
 * Named once and iterated everywhere — seeding, dirty-checking, sending and
 * diffing all walk this list. Five hand-written copies of it is five places for a
 * field to be dropped, and a `recommendation` that is typed, shown, and quietly
 * not sent is the worst of the failures: the radiologist watched themselves write
 * "suggest CT for further characterisation" and the clinician never sees it.
 */
const REPORT_FIELDS = ['clinicalHistory', 'technique', 'findings', 'impression', 'recommendation'];

/** What a template may carry. It has no clinicalHistory and no recommendation on
 *  purpose: both are about THIS patient, and a stock sentence in either is the
 *  copy-paste failure templates were supposed to prevent. */
const TEMPLATE_FIELDS = ['technique', 'findings', 'impression'];

const EMPTY_DRAFT = REPORT_FIELDS.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

/**
 * Study statuses that mean the images exist.
 *
 * A MIRROR of imagingService.SIGNABLE_STUDY_STATUSES, kept so the sign pane can be
 * shut before the click instead of after the refusal. The server is always the
 * authority: if the two ever disagree, the server is right and this constant is
 * the bug. Written out rather than derived from a position in the status list,
 * for the reason the service gives — 'cancelled' sits last in the ENUM, and an
 * index comparison would sweep it in.
 */
const STUDY_REPORTABLE_STATUSES = ['performed', 'reported', 'signed', 'released'];

/** Report statuses whose text may still be typed into. */
const EDITABLE_REPORT_STATUSES = ['draft'];

/**
 * The mandatory PC-PNDT declaration, quoted so the signer knows what the document
 * will carry. It is NEVER inserted into an editable field — see the header.
 */
const PNDT_DECLARATION = 'The sex of the foetus has not been detected or disclosed';

/** How much of an amendment reason is a reason. Four characters rejects "ok" and
 *  "fix" while accepting "typo", which is a real and complete answer. */
const MIN_AMEND_REASON = 4;

const REPORT_STATUS_STYLES = {
    draft: { cls: 'bg-slate-100 text-slate-600 border-slate-300', fallback: 'DRAFT' },
    signed: { cls: 'bg-[#4ABDBD]/15 text-[#2D8A8A] border-[#4ABDBD]/50', fallback: 'SIGNED' },
    released: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', fallback: 'RELEASED' },
    amended: { cls: 'bg-amber-100 text-amber-800 border-amber-300', fallback: 'SUPERSEDED' },
};

const STUDY_STATUS_STYLES = {
    ordered: 'bg-slate-100 text-slate-600 border-slate-300',
    scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
    arrived: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    performed: 'bg-[#4ABDBD]/15 text-[#2D8A8A] border-[#4ABDBD]/50',
    reported: 'bg-violet-50 text-violet-700 border-violet-200',
    signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    released: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
};

/**
 * Escape a value being interpolated into a sweetalert2 `html` body.
 *
 * The confirmations below need more than one sentence, which `text` cannot lay
 * out, so they build markup. Patient names, procedure names and the radiologist's
 * own impression are all free text — an `<img onerror=...>` typed into any of them
 * would otherwise execute in the signer's session at the exact moment they are
 * about to put their registration number on a clinical document. React escapes
 * everything it renders; this is the one place on the screen that bypasses it, so
 * it does the job by hand.
 */
const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ESCAPES[c]);

const fmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    // The time part is the one clock format ("2:30 PM", utils/clock).
    return Number.isNaN(d.getTime())
        ? ''
        : `${d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })}, ${formatTime(d)}`;
};

const fmtDate = (value) => {
    if (!value) return '';
    // A bare 'YYYY-MM-DD' is a calendar fact with no time and no zone. Pushing it
    // through a Date would move a licence expiry to the previous day for anyone
    // west of the server, which is the same trap services/radiology/imagingService
    // documents on clinicDay.
    const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
    if (bare) return `${bare[3]}/${bare[2]}/${bare[1]}`;
    return fmtDateTime(value);
};

const trimmed = (v) => String(v ?? '').trim();

/** The narrative of a report as a plain object, or five empty strings. */
const seedFrom = (report) => REPORT_FIELDS.reduce(
    (acc, key) => ({ ...acc, [key]: report?.[key] ?? '' }),
    {}
);

/* ------------------------------------------------------------------ *
 * Presentational pieces
 *
 * Outside the component on purpose: none of them holds page state, and a
 * component declared inside a render is a NEW component type on every keystroke —
 * React unmounts and remounts it, and a textarea that remounts loses the caret.
 * On a screen whose whole job is typing paragraphs that is not a performance
 * note, it is a broken text box.
 * ------------------------------------------------------------------ */

function ReportStatusChip({ status, version, testId }) {
    const { t } = useTranslation();
    const style = REPORT_STATUS_STYLES[status] || REPORT_STATUS_STYLES.draft;
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${style.cls}`}
        >
            {t(`imagingreporting.report_status_${status}`, style.fallback)}
            {version ? <span className="opacity-70">{` v${version}`}</span> : null}
        </span>
    );
}

function StudyStatusChip({ status }) {
    const { t } = useTranslation();
    const cls = STUDY_STATUS_STYLES[status] || STUDY_STATUS_STYLES.ordered;
    return (
        <span
            data-testid="study-status"
            className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${cls}`}
        >
            {t(`imagingreporting.study_status_${status}`, String(status || '').toUpperCase())}
        </span>
    );
}

/**
 * One narrative block.
 *
 * A TEXTAREA and never a rich editor. What is typed here is rendered into a PDF by
 * PDFKit, which draws text and not markup: anything that produced bold tags or
 * bullet entities would print them literally on the document a patient is handed.
 */
function NarrativeField({
    name, label, hint, value, onChange, rows = 4, disabled, placeholder, emphasis, icon: Icon, testId,
}) {
    return (
        <div className="mb-4">
            <div className="flex items-center justify-between gap-2 mb-1">
                <label htmlFor={`field-${name}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    {Icon && <Icon className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                    {label}
                </label>
                {hint && <span className="text-[11px] text-slate-400 text-right">{hint}</span>}
            </div>
            <textarea
                id={`field-${name}`}
                data-testid={testId || `field-${name}`}
                value={value}
                rows={rows}
                disabled={disabled}
                placeholder={placeholder}
                onChange={(e) => onChange(name, e.target.value)}
                className={`w-full p-3 border-2 rounded-xl text-sm leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] transition-colors resize-y ${
                    disabled
                        ? 'bg-slate-50 border-slate-200 text-slate-600 cursor-not-allowed'
                        : emphasis
                            ? 'bg-white border-[#4ABDBD]/60'
                            : 'bg-white border-gray-300'
                }`}
            />
        </div>
    );
}

/** A read-only narrative block, for a version nobody may touch any more. */
function NarrativeReadonly({ label, value }) {
    if (!trimmed(value)) return null;
    return (
        <div className="mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
            <div className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">{value}</div>
        </div>
    );
}

/** A labelled fact from the acquisition. Renders NOTHING when there is no value —
 *  an empty row reading "Radiographer —" invites the reader to believe the field
 *  was checked and found empty, which is not the same as never recorded. */
function Fact({ label, value, mono }) {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex items-baseline justify-between gap-3 py-1 border-b border-slate-100 last:border-0">
            <span className="text-[11px] text-slate-400 whitespace-nowrap">{label}</span>
            <span className={`text-[11px] text-slate-700 font-semibold text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function ImagingReporting() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { can } = usePermissions();
    const session = useSession();

    // Route param first, query string second. The worklist links with a path; the
    // in-page queue below picks with a query, because editing the query string
    // works on whatever route this page is mounted at and cannot navigate somebody
    // to a URL that does not exist.
    const studyId = params.studyId || searchParams.get('studyId') || '';

    // WHOSE DEPARTMENT. Ignored for a radiologist, who carries their hospital in
    // the verified token and cannot name another; required for Super Admin, who
    // carries none and is answered 400 until the organisation is named. Sent on
    // every call so this screen serves both without a second code path.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    const canDraft = can('radiology.report.draft');
    const canSign = can('radiology.report.sign');

    /* ---- the picker ---- */
    const [queue, setQueue] = useState('to_report');
    const [queueSearch, setQueueSearch] = useState('');
    /* ---- AI pack: dictation -> draft (Phase 4, #15) ---- */
    const { has: hasFeature } = useTenantFeatures();
    const [showDictation, setShowDictation] = useState(false);
    const [queueRows, setQueueRows] = useState([]);
    const [queueCounts, setQueueCounts] = useState({});
    const [queueLoading, setQueueLoading] = useState(false);

    /* ---- the study ---- */
    const [loading, setLoading] = useState(Boolean(studyId));
    const [error, setError] = useState(null);
    // WHICH study the rows below actually describe. Not decoration: without it the
    // frame between choosing a second study and the fetch that answers for it
    // renders the PREVIOUS patient's report under the new patient's name, and a
    // radiologist reading fast has no way to tell. Compared against `studyId` on
    // every render, so the swap is never visible even for a frame.
    const [loadedFor, setLoadedFor] = useState(null);
    // The same fact, reachable from inside `load` without becoming one of its
    // dependencies — see the note there.
    const loadedForRef = useRef(null);
    const [study, setStudy] = useState(null);
    const [patient, setPatient] = useState(null);
    const [procedure, setProcedure] = useState(null);
    const [equipment, setEquipment] = useState(null);
    const [equipmentProblem, setEquipmentProblem] = useState(null);
    const [order, setOrder] = useState(null);
    const [pndt, setPndt] = useState(null);
    const [reports, setReports] = useState([]);
    const [images, setImages] = useState([]);
    const [imagesHeld, setImagesHeld] = useState(0);
    const [transferComplete, setTransferComplete] = useState(true);
    const [viewer, setViewer] = useState(null);

    /* ---- the editor ---- */
    // Held apart from `reports` so the stored text and the typed text are never
    // the same variable: the sign pane has to be able to tell them apart, and a
    // reload that merged them would silently adopt whatever was half-typed.
    const [draftText, setDraftText] = useState(EMPTY_DRAFT);
    const [amending, setAmending] = useState(false);
    const [amendReason, setAmendReason] = useState('');
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [appliedTemplate, setAppliedTemplate] = useState(null);

    const [saving, setSaving] = useState(false);
    const [signing, setSigning] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    /* ---- viewing the pictures ---- */
    const [lightbox, setLightbox] = useState(null);
    const [brokenImages, setBrokenImages] = useState(() => new Set());
    const [openVersion, setOpenVersion] = useState(null);

    /* -------------------------------------------------------------- *
     * Load — the queue
     * -------------------------------------------------------------- */

    const loadQueue = useCallback(async () => {
        if (studyId) return;
        setQueueLoading(true);
        try {
            const body = await dispatch(getRadiologyWorklist({
                queue, search: queueSearch, tenantId, limit: 50,
            })).unwrap();
            setQueueRows(body?.data || []);
            setQueueCounts(body?.counts?.byQueue || {});
            setError(null);
        } catch (err) {
            setError(extractErrorMessage(err, t('imagingreporting.queue_error', 'Could not load the reporting queue')));
        } finally {
            setQueueLoading(false);
        }
    }, [dispatch, studyId, queue, queueSearch, tenantId, t]);

    useEffect(() => {
        // Debounced, because this runs on every keystroke of the search box and a
        // request per character is a queue that flickers between two answers.
        const handle = setTimeout(() => { loadQueue(); }, 250);
        return () => clearTimeout(handle);
    }, [loadQueue]);

    /* -------------------------------------------------------------- *
     * Load — one study
     * -------------------------------------------------------------- */

    const load = useCallback(async () => {
        if (!studyId) {
            setLoading(false);
            return;
        }
        // Which study the rows in state belong to as this run STARTS. Read from a
        // ref rather than from `loadedFor` so it costs nothing in the dependency
        // list — depending on the state would rebuild `load`, refire the effect
        // that calls it, and fetch every study twice.
        const previous = loadedForRef.current;
        setLoading(true);
        try {
            const data = await dispatch(getStudy({ studyId, tenantId })).unwrap() || {};
            setStudy(data.study || null);
            setPatient(data.patient || null);
            setProcedure(data.procedure || null);
            setEquipment(data.equipment || null);
            setEquipmentProblem(data.equipmentProblem || null);
            setOrder(data.order || null);
            setPndt(data.pndt || null);
            setReports(data.reports || []);
            setImages(data.images || []);
            // How many objects the study RECORDS, which is not always how many are
            // showing: a key whose presigned link could not be minted is held and
            // unviewable. Falling back to the count of usable links would report
            // that study as complete, which is the one thing this number exists to
            // contradict.
            setImagesHeld(Number(data.imagesHeld ?? (data.images || []).length) || 0);
            // Present-and-false is the finding; absent is not a claim of trouble.
            setTransferComplete(data.transferComplete !== false);
            setViewer(data.viewer || null);
            // Links that failed a moment ago may simply have expired; the new ones
            // deserve their own chance before being called broken.
            setBrokenImages(new Set());

            // The boxes are seeded from what is STORED, so "unsaved" starts empty
            // and means exactly what it says.
            const live = (data.reports || []).find((r) => r.status !== 'amended') || null;
            setDraftText(seedFrom(live));
            setAmending(false);
            setAmendReason('');
            setAppliedTemplate(null);
            // A 200 that carries no study is not a study with nothing in it. Left
            // unsaid it renders as a blank report form with a blank accession
            // number — a screen somebody could type a whole interpretation into
            // before discovering it belongs to nothing.
            setError(data.study ? null : t('imagingreporting.no_study', 'That study could not be read'));
        } catch (err) {
            // A REFRESH of the study already on screen keeps what is on screen and
            // says it may be stale — that is the honest answer, and a radiologist
            // mid-sentence must not lose the paragraph because a poll failed.
            // A load of a DIFFERENT study that failed is another matter entirely:
            // leaving the previous patient's images and impression up under a new
            // accession number is not staleness, it is the wrong patient.
            if (previous !== studyId) {
                setStudy(null);
                setPatient(null);
                setProcedure(null);
                setEquipment(null);
                setEquipmentProblem(null);
                setOrder(null);
                setPndt(null);
                setReports([]);
                setImages([]);
                setImagesHeld(0);
                setTransferComplete(true);
                setDraftText(EMPTY_DRAFT);
                setAmending(false);
                setAmendReason('');
            }
            setError(extractErrorMessage(err, t('imagingreporting.load_error', 'Could not load this study')));
        } finally {
            // Stamped on the way out of BOTH paths. A study that failed to load has
            // still been answered for; leaving the stamp behind would hold the
            // screen on a spinner that never resolves and hide the reason.
            loadedForRef.current = studyId;
            setLoadedFor(studyId);
            setLoading(false);
        }
    }, [dispatch, studyId, tenantId, t]);

    useEffect(() => { load(); }, [load, reloadKey]);

    /* -------------------------------------------------------------- *
     * Load — the templates for THIS procedure
     *
     * Fetched per procedure rather than all at once: a department's template
     * library is every normal report it has ever agreed on, and a picker holding
     * the MRI spine templates while a chest film is open is a picker somebody
     * eventually chooses from by accident.
     * -------------------------------------------------------------- */

    useEffect(() => {
        let cancelled = false;
        const procedureId = procedure?.procedureId;
        if (!procedureId && !study?.modality) {
            setTemplates([]);
            return undefined;
        }
        setTemplatesLoading(true);
        dispatch(getTemplates({
            procedureId: procedureId || '',
            modality: study?.modality || procedure?.modality || '',
            tenantId,
        }))
            .unwrap()
            .then((body) => {
                if (cancelled) return;
                setTemplates(body?.data || []);
            })
            .catch(() => {
                // A template library that will not load is an inconvenience, not a
                // reason to shout: the radiologist can still type the report, which
                // is what they were going to do before templates existed. The
                // picker says it is empty rather than raising the page banner over
                // a study that is otherwise fine.
                if (!cancelled) setTemplates([]);
            })
            .finally(() => { if (!cancelled) setTemplatesLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch, procedure, study?.modality, tenantId]);

    /* -------------------------------------------------------------- *
     * Derived state
     *
     * NOTE there is no polling here, unlike the worklist. A radiologist mid-
     * sentence must not have the paragraph under their cursor replaced by
     * somebody else's copy of it; a queue is a list being read, this is a
     * document being written.
     * -------------------------------------------------------------- */

    /** The report that is live: the newest one that has not been superseded.
     *  Rows arrive newest-first, and an 'amended' row is history. */
    const liveReport = useMemo(
        () => (reports || []).find((r) => r.status !== 'amended') || null,
        [reports]
    );

    const pastReports = useMemo(
        () => (reports || []).filter((r) => r !== liveReport),
        [reports, liveReport]
    );

    /** What is STORED for the live report — the baseline "unsaved" is measured
     *  against, and the text an amendment starts from. */
    const stored = useMemo(() => seedFrom(liveReport), [liveReport]);

    const changed = useMemo(
        () => REPORT_FIELDS.some((key) => trimmed(draftText[key]) !== trimmed(stored[key])),
        [draftText, stored]
    );

    const hasAnyText = useMemo(
        () => REPORT_FIELDS.some((key) => trimmed(draftText[key])),
        [draftText]
    );

    const studyReportable = STUDY_REPORTABLE_STATUSES.includes(String(study?.status));

    /**
     * Which of the three shapes this screen is in.
     *   'amend'  a correction is being written over a signed report
     *   'edit'   there is no report yet, or the one there is is still a draft
     *   'locked' the live report is signed — its text is immutable
     */
    const mode = useMemo(() => {
        if (amending) return 'amend';
        if (!liveReport) return 'edit';
        return EDITABLE_REPORT_STATUSES.includes(liveReport.status) ? 'edit' : 'locked';
    }, [amending, liveReport]);

    /**
     * May the boxes be typed into — and WHICH right decides that.
     *
     * It is not one right, because the two modes are two different acts. Writing a
     * first interpretation is drafting (radiology.report.draft). Rewriting one that
     * has been SIGNED is a signing-side act (radiology.report.sign): the amendment
     * supersedes a document that carries somebody's registration number, and on a
     * released report it contradicts a copy a clinician is already holding.
     *
     * Gating both on canDraft — as this line used to — put the withdrawal of a
     * signed report in the hands of whoever may type a draft, which is typically a
     * registrar or a transcriptionist. Gating amend mode on canSign without also
     * moving THIS line would be the mirror failure and just as bad: the amend
     * button would open a form the amender is not allowed to type into, so a
     * consultant with sign but not draft would sit in front of read-only boxes
     * with no explanation.
     */
    const editable = studyReportable && ((mode === 'edit' && canDraft) || (mode === 'amend' && canSign));

    const amendReady = mode === 'amend'
        && changed
        && trimmed(amendReason).length >= MIN_AMEND_REASON;

    /**
     * Who is about to sign, and under which number.
     *
     * The backend's study read does not necessarily carry a `viewer` block, and
     * that ABSENCE must not be read as "this person has no registration number" —
     * shutting the pane on a field the payload never claimed to answer would stop
     * a properly registered consultant from signing, with an explanation that is
     * not true. So: a viewer that states a registration is believed in both
     * directions; a viewer that does not mention one leaves the question to the
     * server, which answers it at the signature with SIGNATORY_NOT_REGISTERED and
     * is handled by name below.
     */
    const signerIdentity = useMemo(() => {
        const claimsRegistration = viewer ? Object.prototype.hasOwnProperty.call(viewer, 'registrationNo') : false;
        return {
            // localStorage is read directly for the name only because useSession
            // does not expose it and a name is cosmetic — the signature the server
            // records comes from the verified token, never from here.
            name: viewer?.name || localStorage.getItem('Name') || '',
            registrationNo: viewer?.registrationNo || '',
            // null means "not stated on this payload", which is not the same as
            // "missing" — see above.
            registered: claimsRegistration ? Boolean(viewer.registrationNo) : null,
        };
    }, [viewer]);

    /**
     * Why the sign pane is shut, in one word the render can switch on.
     *
     * ORDERED THE WAY imagingService.assertSignable ORDERS ITS CHECKS: the study
     * before the signatory, so a radiologist who opened tomorrow's booking is told
     * that, rather than being told their registration number is missing while the
     * real problem is that nobody has been scanned yet.
     */
    const signBlockedBy = useMemo(() => {
        if (!canSign) return 'permission';
        if (!studyReportable) return 'study';
        if (mode === 'amend') return 'amending';
        if (!liveReport || !EDITABLE_REPORT_STATUSES.includes(liveReport.status)) return 'nothing';
        if (changed) return 'unsaved';
        if (signerIdentity.registered === false) return 'registration';
        return null;
    }, [canSign, studyReportable, mode, liveReport, changed, signerIdentity]);

    const setField = useCallback((name, value) => {
        setDraftText((d) => ({ ...d, [name]: value }));
    }, []);

    /* -------------------------------------------------------------- *
     * Not losing a dictation
     * -------------------------------------------------------------- */

    // Ctrl/Cmd+S saves the draft. A radiologist reporting a list types for hours
    // with both hands on the keyboard, and a save that needs the mouse is a save
    // that gets postponed until the browser is closed. The handler is reached
    // through a ref so the listener is bound once instead of being torn down and
    // rebuilt on every keystroke.
    const saveRef = useRef(() => {});
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 's') {
                e.preventDefault();
                saveRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // The browser's own "leave site?" prompt, armed only while something typed is
    // not stored. A closed tab is the commonest way a half-written report is lost,
    // and the report is the only part of this screen nobody else can reproduce.
    useEffect(() => {
        if (!editable || !changed) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [editable, changed]);

    // Esc closes the lightbox. A full-screen image with no visible way out on a
    // keyboard is a trap for exactly the user who never touches the mouse.
    useEffect(() => {
        if (!lightbox) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox]);

    /* -------------------------------------------------------------- *
     * Templates
     * -------------------------------------------------------------- */

    const handleApplyTemplate = async (templateId) => {
        const template = templates.find((tpl) => String(tpl.templateId) === String(templateId));
        if (!template || !editable) return;

        // Which boxes this would overwrite, by name. "This will replace your
        // text" is a sentence people click through; "this will replace Findings
        // and Impression" is one they read.
        const wouldOverwrite = TEMPLATE_FIELDS.filter(
            (key) => trimmed(draftText[key]) && trimmed(template[key]) && trimmed(draftText[key]) !== trimmed(template[key])
        );

        if (wouldOverwrite.length) {
            const labels = {
                technique: t('imagingreporting.technique', 'Technique'),
                findings: t('imagingreporting.findings', 'Findings'),
                impression: t('imagingreporting.impression', 'Impression'),
            };
            const confirm = await MySwal.fire({
                title: t('imagingreporting.template_overwrite_title', 'Replace what you have written?'),
                html: `<div style="text-align:left;font-size:14px;color:#334155">
                    <p style="margin-bottom:10px">${t(
                        'imagingreporting.template_overwrite_body',
                        'Loading “{{name}}” will replace the text already in these boxes. Text from one patient’s report left inside another’s is the mistake templates exist to prevent, so this is never done silently.',
                        { name: template.name }
                    )}</p>
                    <ul style="margin:0;padding-left:18px">
                        ${wouldOverwrite.map((key) => `<li><strong>${esc(labels[key])}</strong></li>`).join('')}
                    </ul>
                </div>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('imagingreporting.template_overwrite_confirm', 'Replace'),
                cancelButtonText: t('imagingreporting.cancel', 'Cancel'),
            });
            if (!confirm.isConfirmed) return;
        }

        setDraftText((d) => {
            const next = { ...d };
            TEMPLATE_FIELDS.forEach((key) => {
                // A template that says nothing about a box leaves it alone. A blank
                // stock "Impression" wiping a sentence the radiologist has already
                // written is the template doing damage rather than saving time.
                if (trimmed(template[key])) next[key] = template[key];
            });
            return next;
        });
        setAppliedTemplate(template.name || null);
        // Not a quiet success. A template is text the radiologist did not write and
        // is about to sign, so the confirmation says what has to happen to it next.
        toast.info(t(
            'imagingreporting.template_applied',
            'Loaded “{{name}}”. Read every line of it against these images before you sign.',
            { name: template.name }
        ));
    };

    /* -------------------------------------------------------------- *
     * Save — the DRAFT half
     * -------------------------------------------------------------- */

    const handleSaveDraft = useCallback(async () => {
        if (mode !== 'edit' || !editable || !changed || saving) return;
        if (!hasAnyText) {
            toast.info(t('imagingreporting.nothing_to_save', 'There is nothing to save yet'));
            return;
        }
        setSaving(true);
        try {
            await dispatch(draftReport({
                studyId,
                tenantId,
                ...REPORT_FIELDS.reduce((acc, key) => ({ ...acc, [key]: trimmed(draftText[key]) || null }), {}),
            })).unwrap();
            toast.success(t('imagingreporting.draft_saved', 'Draft saved. It is not signed, and nothing has been sent to the ward.'));
            setReloadKey((k) => k + 1);
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('imagingreporting.draft_error', 'Could not save this draft')));
        } finally {
            setSaving(false);
        }
    }, [mode, editable, changed, saving, hasAnyText, dispatch, studyId, tenantId, draftText, t]);

    // Keep the shortcut pointed at the current closure without rebinding the
    // listener. Written in an effect rather than during the render, so a render
    // React throws away cannot leave the shortcut pointing at a save that closed
    // over text the user never saw committed.
    useEffect(() => { saveRef.current = handleSaveDraft; }, [handleSaveDraft]);

    /* -------------------------------------------------------------- *
     * Sign — the deliberate act
     * -------------------------------------------------------------- */

    const handleSign = async () => {
        if (signBlockedBy || signing || !liveReport) return;

        const lines = [];
        lines.push(`<p style="margin-bottom:10px">${t(
            'imagingreporting.sign_body',
            'You are putting your name to this interpretation. From the moment it is signed the text cannot be edited — a correction is a new version that supersedes it, and the original stays on the record.'
        )}</p>`);

        // WHO IS SIGNING, and under which number. Shown before the click and not
        // merely recorded after it: a consultant signing from a registrar's
        // unlocked workstation is the specific accident this line prevents.
        lines.push(`<div style="margin-bottom:10px;padding:10px 12px;border-radius:10px;background:#f1f5f9;font-size:13px;color:#334155">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#64748b;font-weight:700">${t('imagingreporting.signing_as', 'Signing as')}</div>
            <div style="font-weight:700">${esc(signerIdentity.name || t('imagingreporting.this_account', 'this account'))}</div>
            ${
                signerIdentity.registrationNo
                    ? `<div style="font-size:12px;color:#475569">${t('imagingreporting.reg_no', 'Reg. no')} ${esc(signerIdentity.registrationNo)}</div>`
                    : `<div style="font-size:12px;color:#92400e">${t('imagingreporting.reg_no_unknown', 'Your registration number will be taken from your staff record and printed on the report. If there is none on file the signature will be refused.')}</div>`
            }
        </div>`);

        // What is actually being signed, in the words that will print. A signer
        // who has scrolled away from the impression is signing a summary they
        // cannot currently see.
        const impression = trimmed(draftText.impression);
        lines.push(`<div style="margin-bottom:10px;font-size:13px;color:#334155">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#64748b;font-weight:700">${t('imagingreporting.impression', 'Impression')}</div>
            ${
                impression
                    ? `<div style="white-space:pre-wrap">${esc(impression.length > 400 ? `${impression.slice(0, 400)}…` : impression)}</div>`
                    : `<div style="color:#b45309">${t('imagingreporting.no_impression_warning', 'There is no impression on this report. The referring clinician reads that line first and often reads nothing else.')}</div>`
            }
        </div>`);

        if (!transferComplete) {
            lines.push(`<p style="margin-bottom:10px;padding:8px 10px;border-radius:8px;background:#fef2f2;color:#991b1b;font-size:13px">
                ${t(
                    'imagingreporting.sign_transfer_warning',
                    'The console recorded {{expected}} image(s) and only {{held}} arrived. You may be reporting a partial series.',
                    { expected: study?.imageCount ?? 0, held: imagesHeld }
                )}
            </p>`);
        } else if (imagesHeld === 0) {
            lines.push(`<p style="margin-bottom:10px;padding:8px 10px;border-radius:8px;background:#fffbeb;color:#92400e;font-size:13px">
                ${t('imagingreporting.sign_no_images', 'No images are attached to this study on the system. Confirm you have read them somewhere before signing.')}
            </p>`);
        }

        if (pndt?.applies) {
            lines.push(`<p style="margin-bottom:0;padding:8px 10px;border-radius:8px;background:#f5f3ff;color:#5b21b6;font-size:13px">
                ${t(
                    'imagingreporting.sign_pndt',
                    'This is a PC-PNDT examination. The report will carry the mandatory declaration: “{{line}}”.',
                    { line: PNDT_DECLARATION }
                )}
            </p>`);
        }

        const confirm = await MySwal.fire({
            title: t('imagingreporting.sign_title', 'Sign this report?'),
            html: `<div style="text-align:left;font-size:14px;color:#334155">${lines.join('')}</div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2D8A8A',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('imagingreporting.sign_confirm', 'Sign the report'),
            cancelButtonText: t('imagingreporting.cancel', 'Cancel'),
        });
        if (!confirm.isConfirmed) return;

        setSigning(true);
        try {
            await dispatch(signReport({ reportId: liveReport.reportId, tenantId })).unwrap();
            toast.success(t('imagingreporting.signed', 'Report signed. Handing it over is a separate act on the reports screen.'));
            setReloadKey((k) => k + 1);
        } catch (err) {
            // `unwrap()` throws the slice's rejection payload, not the axios error,
            // so the backend's code sits at the top level. That payload is the whole
            // point of routing through the slice: these three refusals each have a
            // different next action, and none of them is "try again".
            const code = err?.code;
            if (code === 'SIGNATORY_NOT_REGISTERED' || code === 'SIGNATORY_UNKNOWN') {
                await MySwal.fire({
                    title: t('imagingreporting.not_registered_title', 'No registration number on your record'),
                    text: t(
                        'imagingreporting.not_registered_text',
                        'A radiology report carries the registration number of whoever signed it, so it cannot be signed without one. An administrator has to add it to your record — nothing you type on this screen will get past it. Your draft is saved.'
                    ),
                    icon: 'error',
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('imagingreporting.done', 'Done'),
                });
            } else if (code === 'STUDY_NOT_PERFORMED') {
                await MySwal.fire({
                    title: t('imagingreporting.not_performed_title', 'Nothing has been scanned yet'),
                    text: extractErrorMessage(err, t(
                        'imagingreporting.not_performed_text',
                        'The images for this study have not been acquired, so there is nothing to put a signature to.'
                    )),
                    icon: 'warning',
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('imagingreporting.done', 'Done'),
                });
                setReloadKey((k) => k + 1);
            } else if (code === 'ILLEGAL_REPORT_TRANSITION') {
                // Somebody else moved this report while it was open here. Reloading
                // is the fix; retrying blindly would sign whatever it has become.
                toast.warn(extractErrorMessage(err, t('imagingreporting.raced', 'This report has changed since you opened it — reloading')));
                setReloadKey((k) => k + 1);
            } else {
                toast.warn(extractErrorMessage(err, t('imagingreporting.sign_error', 'Could not sign this report')));
            }
        } finally {
            setSigning(false);
        }
    };

    /* -------------------------------------------------------------- *
     * Amend — correcting something that has been signed
     * -------------------------------------------------------------- */

    const handleStartAmend = async () => {
        // canSIGN, not canDraft. Amending withdraws a report that has already been
        // signed — and often released, meaning a clinician is holding a copy of
        // what it used to say. That is the signatory's act: only somebody entitled
        // to put their name to an interpretation may supersede one. Gated on the
        // drafting right, a registrar could retire a consultant's signed report.
        if (!liveReport || !canSign) return;
        const released = liveReport.status === 'released';
        const confirm = await MySwal.fire({
            title: t('imagingreporting.amend_title', 'Amend this report?'),
            html: `<div style="text-align:left;font-size:14px;color:#334155">
                <p style="margin-bottom:10px">${t(
                    'imagingreporting.amend_body',
                    'This does not edit the signed report. A new version is issued, the signed one keeps its own text and is marked superseded, and the amendment has to be signed like any other report.'
                )}</p>
                ${
                    released
                        ? `<p style="margin-bottom:10px;padding:8px 10px;border-radius:8px;background:#fef2f2;color:#991b1b;font-size:13px">${t(
                            'imagingreporting.amend_released',
                            'This version has already been handed over. Somebody is holding a copy of what it says, so the amendment reason is what tells a reader why the two documents differ.'
                        )}</p>`
                        : ''
                }
                <p style="font-size:13px;color:#475569">${t('imagingreporting.amend_current', 'Superseding')} <strong>${esc(liveReport.reportNo || liveReport.reportId)}</strong> ${t('imagingreporting.version_n', 'v{{n}}', { n: liveReport.version || 1 })}</p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('imagingreporting.amend_start', 'Write the amendment'),
            cancelButtonText: t('imagingreporting.cancel', 'Cancel'),
        });
        if (!confirm.isConfirmed) return;

        // Seeded from the signed text so the radiologist corrects a sentence
        // rather than retyping a report — which is how a paragraph that was right
        // gets lost along with the one that was wrong.
        setDraftText(seedFrom(liveReport));
        setAmendReason('');
        setAmending(true);
        setOpenVersion(null);
    };

    const handleCancelAmend = () => {
        setDraftText(seedFrom(liveReport));
        setAmendReason('');
        setAmending(false);
    };

    const handleIssueAmendment = async () => {
        if (!amendReady || saving || !liveReport) return;
        setSaving(true);
        try {
            const body = await dispatch(amendReport({
                reportId: liveReport.reportId,
                tenantId,
                amendReason: trimmed(amendReason),
                ...REPORT_FIELDS.reduce((acc, key) => ({ ...acc, [key]: trimmed(draftText[key]) || null }), {}),
            })).unwrap();
            const next = body?.data?.report || body?.report || null;
            toast.success(t(
                'imagingreporting.amended',
                'Amendment issued as version {{n}}. It is a draft — it still has to be signed.',
                { n: next?.version || (liveReport.version || 1) + 1 }
            ));
            setAmending(false);
            setAmendReason('');
            setReloadKey((k) => k + 1);
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('imagingreporting.amend_error', 'Could not issue the amendment')));
        } finally {
            setSaving(false);
        }
    };

    /* -------------------------------------------------------------- *
     * Picking a study
     * -------------------------------------------------------------- */

    const openStudy = (id) => {
        const next = new URLSearchParams(searchParams);
        next.set('studyId', id);
        setSearchParams(next);
    };

    const clearStudy = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('studyId');
        setSearchParams(next);
    };

    /* -------------------------------------------------------------- *
     * Render — the picker
     * -------------------------------------------------------------- */

    if (!studyId) {
        const QUEUES = [
            { id: 'to_report', label: t('imagingreporting.queue_to_report', 'Waiting to be reported') },
            { id: 'to_sign', label: t('imagingreporting.queue_to_sign', 'Drafted, waiting for a signature') },
        ];
        return (
            <div data-testid="imaging-reporting-picker" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                    <div>
                        <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2">
                            <ScanLine className="w-5 h-5 text-[#4ABDBD]" />
                            {t('imagingreporting.title', 'Reporting')}
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {t('imagingreporting.picker_hint', 'Open a performed study to write its interpretation. Only studies whose images have been acquired can be reported.')}
                        </p>
                    </div>
                    <input
                        data-testid="queue-search"
                        type="text"
                        value={queueSearch}
                        maxLength={60}
                        onChange={(e) => setQueueSearch(e.target.value)}
                        placeholder={t('imagingreporting.search_placeholder', 'Accession, patient, procedure')}
                        className="w-full sm:w-72 p-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    {QUEUES.map((q) => (
                        <button
                            key={q.id}
                            type="button"
                            data-testid={`queue-tab-${q.id}`}
                            onClick={() => setQueue(q.id)}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer border-2 transition-colors ${
                                queue === q.id
                                    ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#2D8A8A]'
                                    : 'border-gray-200 text-slate-500 hover:border-[#4ABDBD]/40'
                            }`}
                        >
                            {q.label}
                            <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-bold">
                                {queueCounts?.[q.id] ?? 0}
                            </span>
                        </button>
                    ))}
                </div>

                {error && (
                    <div className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {queueLoading && !queueRows.length ? (
                    <div className="flex flex-col items-center justify-center py-10">
                        <CommonLoader />
                        <span className="text-xl text-[#4ABDBD]">{t('imagingreporting.loading', 'Loading...')}</span>
                    </div>
                ) : queueRows.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm border border-gray-200 rounded-xl">
                        {queue === 'to_report'
                            ? t('imagingreporting.queue_empty_report', 'Nothing is waiting to be reported.')
                            : t('imagingreporting.queue_empty_sign', 'Nothing is waiting for a signature.')}
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-gray-200">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-slate-100 text-slate-600">
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('imagingreporting.accession', 'Accession')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('imagingreporting.patient', 'Patient')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('imagingreporting.examination', 'Examination')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('imagingreporting.performed', 'Performed')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('imagingreporting.images', 'Images')}</th>
                                    <th className="p-2.5" />
                                </tr>
                            </thead>
                            <tbody>
                                {queueRows.map((row) => (
                                    <tr
                                        key={row.studyId}
                                        data-testid={`queue-row-${row.studyId}`}
                                        onClick={() => openStudy(row.studyId)}
                                        className="border-b border-gray-100 hover:bg-[#4ABDBD]/5 cursor-pointer"
                                    >
                                        <td className="p-2.5">
                                            <span className="font-mono text-sm font-black text-slate-800 whitespace-nowrap">
                                                {row.accessionNo || '—'}
                                            </span>
                                            <div className="flex flex-wrap items-center gap-1 mt-1">
                                                {row.priority === 'stat' && (
                                                    <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-black uppercase tracking-widest">STAT</span>
                                                )}
                                                {row.priority === 'urgent' && (
                                                    <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest">URGENT</span>
                                                )}
                                                {row.pndtRequired && (
                                                    <span
                                                        title={t('imagingreporting.pndt_row_hint', 'PC-PNDT applies to this examination')}
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold uppercase"
                                                    >
                                                        <Baby className="w-3 h-3" />
                                                        PC-PNDT
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-2.5">
                                            <div className="text-sm font-semibold text-slate-700">{row.patientName || t('common.patient_label', 'Patient')}</div>
                                            <div className="text-[11px] text-slate-400">
                                                {[row.patientAge, row.patientGender, hospitalPatientCode(row)].filter(Boolean).join(' · ')}
                                            </div>
                                        </td>
                                        <td className="p-2.5">
                                            <div className="text-sm text-slate-700">{row.procedureName}</div>
                                            <div className="text-[11px] text-slate-400 uppercase tracking-wide">
                                                {[row.modality, row.bodyPart].filter(Boolean).join(' · ')}
                                            </div>
                                        </td>
                                        <td className="p-2.5 text-[11px] text-slate-500 whitespace-nowrap">
                                            {fmtDateTime(row.performedAt) || '—'}
                                        </td>
                                        <td className="p-2.5">
                                            <span className={`text-[11px] font-semibold ${
                                                Number(row.imageCount || 0) > Number(row.imagesHeld || 0) ? 'text-amber-700' : 'text-slate-500'
                                            }`}
                                            >
                                                {row.imagesHeld ?? 0}
                                                {Number(row.imageCount || 0) > Number(row.imagesHeld || 0)
                                                    ? ` / ${row.imageCount}`
                                                    : ''}
                                            </span>
                                        </td>
                                        <td className="p-2.5 text-right">
                                            <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    /* -------------------------------------------------------------- *
     * Render — one study
     * -------------------------------------------------------------- */

    // `loadedFor !== studyId` covers the frames between picking a study and the
    // effect that fetches it — the render where the rows on screen still belong to
    // the study before this one. See loadedFor.
    if (loading || loadedFor !== studyId) {
        return (
            <div className="bg-white p-6 rounded-2xl shadow-md w-full">
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('imagingreporting.loading', 'Loading...')}</span>
                </div>
            </div>
        );
    }

    if (error && !study) {
        return (
            <div data-testid="imaging-reporting-error" className="bg-white p-6 rounded-2xl shadow-md w-full">
                <button
                    type="button"
                    onClick={() => navigate('/radiology/worklist')}
                    className="inline-flex items-center gap-2 text-sm text-[#2D8A8A] font-semibold cursor-pointer mb-4"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('imagingreporting.back', 'Back to the worklist')}
                </button>
                <div className="flex items-center gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            </div>
        );
    }

    const priority = String(order?.priority || 'routine').toLowerCase();
    const pickedFromQueue = !params.studyId;

    return (
        <div data-testid="imaging-reporting-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full pb-44">
            {/* ---- who and what ---- */}
            <div className="flex flex-col gap-4 mb-5">
                <div className="flex flex-wrap items-center gap-4">
                    <button
                        type="button"
                        data-testid="back-to-worklist"
                        onClick={() => navigate('/radiology/worklist')}
                        className="inline-flex items-center gap-2 text-sm text-[#2D8A8A] font-semibold cursor-pointer w-fit"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('imagingreporting.back', 'Back to the worklist')}
                    </button>
                    {pickedFromQueue && (
                        <button
                            type="button"
                            data-testid="back-to-queue"
                            onClick={clearStudy}
                            className="inline-flex items-center gap-2 text-sm text-slate-500 font-semibold cursor-pointer w-fit"
                        >
                            <Layers className="w-4 h-4" />
                            {t('imagingreporting.choose_another', 'Choose another study')}
                        </button>
                    )}
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-blue-50/30 border border-gray-200">
                    <div className="flex items-start gap-4">
                        {/* The accession number, drawn as every other radiology
                            screen draws it — this is the string on the film jacket,
                            in the archive and on the request form. */}
                        <span
                            data-testid="accession-no"
                            className="inline-flex items-center px-3 py-2 rounded-xl bg-slate-900 text-white shadow-md font-mono text-xl leading-none font-black tracking-wider whitespace-nowrap"
                        >
                            {study?.accessionNo || '—'}
                        </span>
                        <div>
                            <h1 data-testid="patient-name" className="text-xl font-bold text-slate-800">
                                {patient?.name || t('imagingreporting.unknown_patient', 'Unknown patient')}
                            </h1>
                            <div className="text-xs text-slate-500 mt-0.5">
                                {[patient?.age, patient?.gender, hospitalPatientCode(patient)].filter(Boolean).join(' · ')}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                <StudyStatusChip status={study?.status} />
                                {priority === 'stat' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-600 text-white text-[11px] font-black uppercase tracking-widest">
                                        STAT
                                    </span>
                                )}
                                {priority === 'urgent' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-500 text-white text-[11px] font-black uppercase tracking-widest">
                                        URGENT
                                    </span>
                                )}
                                {order?.doctorName && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                        <Stethoscope className="w-3 h-3" />
                                        {order.doctorName}
                                        {order.referrerRegNo ? ` · ${order.referrerRegNo}` : ''}
                                    </span>
                                )}
                                {study?.performedAt && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                        <Clock className="w-3 h-3" />
                                        {t('imagingreporting.performed_at', 'Performed {{when}}', { when: fmtDateTime(study.performedAt) })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="text-left lg:text-right">
                        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            {t('imagingreporting.examination', 'Examination')}
                        </div>
                        <div className="text-sm text-slate-700 font-semibold">
                            {study?.procedureName || procedure?.name || '—'}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wide">
                            {[study?.modality || procedure?.modality, procedure?.bodyPart].filter(Boolean).join(' · ')}
                        </div>
                        {/* AI pack (Phase 4, #15): dictate -> draft. The draft is
                            copied into the form by hand; signing stays human. */}
                        {hasFeature('ai_radiology_dictation') && (
                            <button
                                type="button"
                                data-testid="imaging-dictate-open"
                                onClick={() => setShowDictation(true)}
                                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#4ABDBD]/10 text-[#2D8A8A] text-xs font-semibold hover:bg-[#4ABDBD]/20 transition-colors cursor-pointer"
                            >
                                <Sparkles className="w-3.5 h-3.5" />
                                {t('imagingreporting.dictate', 'Dictate to draft')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <DictationPanel open={showDictation} onClose={() => setShowDictation(false)} />

            {/* A refresh that failed while a report is already on screen. Said out
                loud rather than swallowed: what is displayed is now of unknown age,
                and somebody about to sign needs to know that. */}
            {error && (
                <div data-testid="imaging-reporting-stale" className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* ---- the study has not been scanned ---- */}
            {!studyReportable && (
                <div data-testid="not-performed-banner" className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-slate-50 border-2 border-slate-300">
                    <Lock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-700">
                        <div className="font-bold">
                            {t('imagingreporting.not_performed_title2', 'This study has not been performed')}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">
                            {t(
                                'imagingreporting.not_performed_hint',
                                'Nothing can be written about images that have not been acquired. The radiographer records the examination at the console; the study appears in the reporting queue the moment they do.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            {/* ---- the series may be incomplete ---- */}
            {studyReportable && !transferComplete && (
                <div data-testid="transfer-incomplete" className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-red-50 border-2 border-red-300">
                    <ImageOff className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <div className="font-bold">
                            {t(
                                'imagingreporting.transfer_incomplete_title',
                                'Only {{held}} of {{expected}} images have arrived',
                                { held: imagesHeld, expected: study?.imageCount ?? 0 }
                            )}
                        </div>
                        <p className="text-xs text-red-700 mt-0.5">
                            {t(
                                'imagingreporting.transfer_incomplete_hint',
                                'The transfer from the modality is incomplete. Reporting on part of a series is how a finding on the missing slices is reported as absent — check the console before you write anything.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            {/* ---- the machine's registration ---- */}
            {equipmentProblem && (
                <div data-testid="equipment-problem" className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border-2 border-amber-300">
                    <Radiation className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <div className="font-bold">{t('imagingreporting.equipment_problem_title', 'A registration problem is recorded against this machine')}</div>
                        <p className="text-xs text-amber-800 mt-0.5">
                            {extractErrorMessage(equipmentProblem, t(
                                'imagingreporting.equipment_problem_hint',
                                'The images were made on a machine whose registration was not in order. That belongs in the department’s record, and it is not something a report should be signed over without somebody knowing.'
                            ))}
                        </p>
                    </div>
                </div>
            )}

            {/* ---- PC-PNDT ---- */}
            {pndt?.applies && (
                <div data-testid="pndt-panel" className="mb-4 p-4 rounded-xl bg-violet-50 border-2 border-violet-200">
                    <div className="flex items-start gap-3">
                        <Baby className="w-5 h-5 text-violet-700 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-violet-900 w-full">
                            <div className="font-bold">{t('imagingreporting.pndt_title', 'PC-PNDT applies to this examination')}</div>
                            <p className="text-xs text-violet-800 mt-1">
                                {t(
                                    'imagingreporting.pndt_declaration_hint',
                                    'The signed report carries this line, added to the document itself. Do not type it into the findings — it would print twice, and a statutory declaration sitting in an editable box is one somebody can delete.'
                                )}
                            </p>
                            <div className="mt-2 px-3 py-2 rounded-lg bg-white border border-violet-200 text-[13px] text-violet-900 italic">
                                “{PNDT_DECLARATION}”
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px]">
                                {pndt.form?.formNo && (
                                    <span className="inline-flex items-center gap-1 text-violet-800">
                                        <FileText className="w-3 h-3" />
                                        {t('imagingreporting.form_f_no', 'Form F {{no}}', { no: pndt.form.formNo })}
                                    </span>
                                )}
                                {pndt.cleared ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                        <CheckCircle2 className="w-3 h-3" />
                                        {t('imagingreporting.form_f_complete', 'Form F complete')}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                                        <AlertTriangle className="w-3 h-3" />
                                        {t('imagingreporting.form_f_missing', 'No completed Form F is recorded against this study')}
                                    </span>
                                )}
                                {pndt.form?.retainUntil && (
                                    <span className="text-violet-700">
                                        {t('imagingreporting.retain_until', 'Retain until {{date}}', { date: fmtDate(pndt.form.retainUntil) })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                {/* ============================ THE REPORT ============================ */}
                <div className="xl:col-span-2">
                    {/* ---- which version is on screen ---- */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold uppercase tracking-wider text-[#2D8A8A]">
                                {mode === 'amend'
                                    ? t('imagingreporting.amendment_heading', 'Amendment')
                                    : t('imagingreporting.report_heading', 'Report')}
                            </h2>
                            {liveReport && (
                                <>
                                    <span className="font-mono text-xs text-slate-500">{liveReport.reportNo}</span>
                                    <ReportStatusChip status={liveReport.status} version={liveReport.version} testId="live-report-status" />
                                </>
                            )}
                            {!liveReport && studyReportable && (
                                <span className="text-[11px] text-slate-400">
                                    {t('imagingreporting.no_report_yet', 'nothing written yet')}
                                </span>
                            )}
                        </div>

                        {/* The affordance is gated on the same right as the act it
                            starts — see handleStartAmend. Drawing it from canDraft
                            offered a button that the backend then refuses, which
                            reads to a registrar as a broken screen rather than as
                            "this is not yours to do". */}
                        {mode === 'locked' && canSign && (
                            <button
                                type="button"
                                data-testid="start-amend"
                                onClick={handleStartAmend}
                                className="inline-flex items-center gap-1.5 text-[12px] text-amber-700 font-semibold underline underline-offset-2 cursor-pointer"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                                {t('imagingreporting.amend', 'Amend — issue a new version')}
                            </button>
                        )}
                    </div>

                    {/* ---- signed: what this document already says ---- */}
                    {mode === 'locked' && liveReport && (
                        <div data-testid="signed-banner" className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-[#4ABDBD]/10 border border-[#4ABDBD]/40">
                            <ShieldCheck className="w-5 h-5 text-[#2D8A8A] flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-[#1f6b6b]">
                                <div className="font-bold text-sm">
                                    {liveReport.status === 'released'
                                        ? t('imagingreporting.released_line', 'Signed and handed over')
                                        : t('imagingreporting.signed_line', 'Signed. Handing it over is a separate act on the reports screen.')}
                                </div>
                                <div className="mt-0.5">
                                    {[
                                        liveReport.signedByName,
                                        liveReport.signedByRegNo ? `${t('imagingreporting.reg_no', 'Reg. no')} ${liveReport.signedByRegNo}` : '',
                                        fmtDateTime(liveReport.signedAt),
                                    ].filter(Boolean).join(' · ')}
                                </div>
                                <div className="mt-1 opacity-80">
                                    {t('imagingreporting.locked_hint', 'The text is fixed by the signature. A correction is a new version — it never rewrites this one.')}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ---- amending: what is about to happen, and why ---- */}
                    {mode === 'amend' && (
                        <div data-testid="amend-panel" className="mb-4 p-4 rounded-xl bg-amber-50 border-2 border-amber-300">
                            <div className="flex items-start gap-3">
                                <History className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                                <div className="w-full">
                                    <div className="text-sm font-bold text-amber-900">
                                        {t(
                                            'imagingreporting.amend_writing_title',
                                            'Writing version {{n}}, superseding {{no}}',
                                            {
                                                n: (liveReport?.version || 1) + 1,
                                                no: liveReport?.reportNo || liveReport?.reportId,
                                            }
                                        )}
                                    </div>
                                    <p className="text-xs text-amber-800 mt-0.5">
                                        {t(
                                            'imagingreporting.amend_writing_hint',
                                            'The signed report is not being edited. It keeps its own text and is marked superseded; this becomes a new draft that has to be signed like any other report.'
                                        )}
                                    </p>
                                    <label htmlFor="amend-reason" className="block text-[11px] font-bold uppercase tracking-wider text-amber-800 mt-3 mb-1">
                                        {t('imagingreporting.amend_reason', 'Why is it being amended?')}
                                    </label>
                                    <input
                                        id="amend-reason"
                                        data-testid="amend-reason"
                                        type="text"
                                        value={amendReason}
                                        maxLength={300}
                                        onChange={(e) => setAmendReason(e.target.value)}
                                        placeholder={t('imagingreporting.amend_reason_placeholder', 'e.g. left/right side transposed; fracture seen on review of the lateral')}
                                        className="w-full p-2.5 border-2 border-amber-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    />
                                    <p className="text-[11px] text-amber-700 mt-1">
                                        {t(
                                            'imagingreporting.amend_reason_hint',
                                            'This prints on the amended report. It is the only thing that tells a clinician holding both versions why they differ.'
                                        )}
                                    </p>
                                    <button
                                        type="button"
                                        data-testid="cancel-amend"
                                        onClick={handleCancelAmend}
                                        className="mt-2 text-[11px] text-slate-500 font-semibold underline underline-offset-2 cursor-pointer"
                                    >
                                        {t('imagingreporting.amend_cancel', 'Abandon this amendment')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ---- the template picker ---- */}
                    {editable && (
                        <div data-testid="template-picker" className="mb-4 p-3 rounded-xl border border-gray-200 bg-slate-50">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <label htmlFor="template-select" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                                    <Layers className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                    {t('imagingreporting.template', 'Reporting template')}
                                </label>
                                <select
                                    id="template-select"
                                    data-testid="template-select"
                                    // Never bound to a chosen value: applying is an ACTION, not a
                                    // selection, and a select that remembered its choice would look
                                    // like the report is "made of" that template — which is exactly
                                    // the belief that gets a normal template signed over an abnormal
                                    // film.
                                    value=""
                                    disabled={templatesLoading || !templates.length}
                                    onChange={(e) => { if (e.target.value) handleApplyTemplate(e.target.value); }}
                                    className="tt-select flex-1 p-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] disabled:bg-slate-100 disabled:text-slate-400"
                                >
                                    <option value="">
                                        {templatesLoading
                                            ? t('imagingreporting.templates_loading', 'Loading templates…')
                                            : templates.length
                                                ? t('imagingreporting.template_choose', 'Load a template…')
                                                : t('imagingreporting.templates_none', 'No template is configured for this examination')}
                                    </option>
                                    {templates.map((tpl) => (
                                        <option key={tpl.templateId} value={tpl.templateId}>
                                            {tpl.name}
                                            {tpl.isDefault ? ` · ${t('imagingreporting.template_default', 'default')}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1.5">
                                {appliedTemplate
                                    ? t(
                                        'imagingreporting.template_applied_hint',
                                        'Loaded from “{{name}}”. Every line of it is now YOUR report — read it against these images before you sign.',
                                        { name: appliedTemplate }
                                    )
                                    : t(
                                        'imagingreporting.template_hint',
                                        'A template fills technique, findings and impression. It is never loaded by itself, and it never touches the clinical history — that belongs to this patient.'
                                    )}
                            </p>
                        </div>
                    )}

                    {/* ---- the narrative ---- */}
                    <NarrativeField
                        name="clinicalHistory"
                        icon={ClipboardList}
                        label={t('imagingreporting.clinical_history', 'Clinical history')}
                        hint={t('imagingreporting.clinical_history_hint', 'why this scan was asked for')}
                        value={draftText.clinicalHistory}
                        onChange={setField}
                        rows={2}
                        disabled={!editable}
                        placeholder={t('imagingreporting.clinical_history_placeholder', 'e.g. fall on outstretched hand, pain over the scaphoid')}
                    />
                    <NarrativeField
                        name="technique"
                        icon={ScanLine}
                        label={t('imagingreporting.technique', 'Technique')}
                        hint={t('imagingreporting.technique_hint', 'what was done, in the report’s own words')}
                        value={draftText.technique}
                        onChange={setField}
                        rows={2}
                        disabled={!editable}
                        placeholder={t('imagingreporting.technique_placeholder', 'e.g. PA and lateral projections; axial sections with IV contrast')}
                    />
                    <NarrativeField
                        name="findings"
                        icon={Images}
                        label={t('imagingreporting.findings', 'Findings')}
                        value={draftText.findings}
                        onChange={setField}
                        rows={10}
                        disabled={!editable}
                        emphasis
                        placeholder={t('imagingreporting.findings_placeholder', 'What the images show.')}
                    />
                    <NarrativeField
                        name="impression"
                        icon={FileText}
                        label={t('imagingreporting.impression', 'Impression')}
                        hint={t('imagingreporting.impression_hint', 'the line the referrer reads first')}
                        value={draftText.impression}
                        onChange={setField}
                        rows={4}
                        disabled={!editable}
                        emphasis
                        placeholder={t('imagingreporting.impression_placeholder', 'The answer to the question the scan was asked.')}
                    />
                    <NarrativeField
                        name="recommendation"
                        icon={ChevronRight}
                        label={t('imagingreporting.recommendation', 'Recommendation')}
                        hint={t('imagingreporting.recommendation_hint', 'optional — what should happen next')}
                        value={draftText.recommendation}
                        onChange={setField}
                        rows={2}
                        disabled={!editable}
                        placeholder={t('imagingreporting.recommendation_placeholder', 'e.g. correlate clinically; CT if symptoms persist')}
                    />

                    {/* This explains why the boxes above are dead, so it must not
                        appear when they are alive. An amendment is open only to
                        somebody with the SIGNING right (see editable), so a
                        consultant who may sign but not draft is typing into live
                        boxes under a notice telling them they may not — the banner
                        would be contradicting the screen it is printed on. */}
                    {!canDraft && mode !== 'amend' && (
                        <div data-testid="cannot-draft" className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-xs">
                            <Lock className="w-4 h-4 flex-shrink-0" />
                            {t('imagingreporting.cannot_draft', 'You may read this study, not report it. Drafting an interpretation needs radiology.report.draft.')}
                        </div>
                    )}

                    {/* ---- earlier versions ---- */}
                    {pastReports.length > 0 && (
                        <div data-testid="past-versions" className="mt-6">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-2">
                                <History className="w-4 h-4" />
                                {t('imagingreporting.earlier_versions', 'Earlier versions')}
                            </h3>
                            {/* Kept on the screen rather than behind a link: the
                                question an inquiry asks is what the FIRST version
                                said, and a superseded report that is hard to find is
                                one that gets quoted from memory. */}
                            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                                {pastReports.map((r) => (
                                    <div key={r.reportId} data-testid={`version-${r.reportId}`} className="p-3">
                                        <button
                                            type="button"
                                            onClick={() => setOpenVersion(openVersion === r.reportId ? null : r.reportId)}
                                            className="w-full flex flex-wrap items-center justify-between gap-2 text-left cursor-pointer"
                                        >
                                            <span className="flex flex-wrap items-center gap-2">
                                                <span className="font-mono text-xs text-slate-600">{r.reportNo}</span>
                                                <ReportStatusChip status={r.status} version={r.version} />
                                                <span className="text-[11px] text-slate-400">
                                                    {[
                                                        r.signedByName,
                                                        r.signedByRegNo,
                                                        fmtDateTime(r.signedAt || r.draftedAt),
                                                    ].filter(Boolean).join(' · ')}
                                                </span>
                                            </span>
                                            <span className="text-[11px] text-[#2D8A8A] font-semibold">
                                                {openVersion === r.reportId
                                                    ? t('imagingreporting.hide', 'Hide')
                                                    : t('imagingreporting.read', 'Read')}
                                            </span>
                                        </button>
                                        {r.amendReason && (
                                            <div className="text-[11px] text-amber-700 mt-1">
                                                {t('imagingreporting.amended_because', 'Amended: {{why}}', { why: r.amendReason })}
                                            </div>
                                        )}
                                        {openVersion === r.reportId && (
                                            <div className="mt-2 pt-2 border-t border-gray-100">
                                                <NarrativeReadonly label={t('imagingreporting.clinical_history', 'Clinical history')} value={r.clinicalHistory} />
                                                <NarrativeReadonly label={t('imagingreporting.technique', 'Technique')} value={r.technique} />
                                                <NarrativeReadonly label={t('imagingreporting.findings', 'Findings')} value={r.findings} />
                                                <NarrativeReadonly label={t('imagingreporting.impression', 'Impression')} value={r.impression} />
                                                <NarrativeReadonly label={t('imagingreporting.recommendation', 'Recommendation')} value={r.recommendation} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* ============================ THE PICTURES ============================ */}
                <div className="xl:col-span-1">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <h2 className="text-sm font-bold uppercase tracking-wider text-[#2D8A8A] inline-flex items-center gap-2">
                            <Images className="w-4 h-4" />
                            {t('imagingreporting.images', 'Images')}
                            <span className="text-slate-400 font-mono normal-case tracking-normal">
                                {transferComplete ? imagesHeld : `${imagesHeld}/${study?.imageCount ?? 0}`}
                            </span>
                        </h2>
                        {/* The links are presigned and expire. Reloading the study is
                            what mints fresh ones, so the button says what it does
                            rather than looking like a refresh of the report. */}
                        <button
                            type="button"
                            data-testid="refresh-images"
                            onClick={() => setReloadKey((k) => k + 1)}
                            title={t('imagingreporting.refresh_images_hint', 'Image links expire. This asks for new ones.')}
                            className="inline-flex items-center gap-1 text-[11px] text-[#2D8A8A] font-semibold cursor-pointer"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            {t('imagingreporting.refresh_links', 'Refresh links')}
                        </button>
                    </div>

                    {images.length === 0 ? (
                        <div data-testid="no-images" className="p-6 text-center text-xs text-slate-500 border border-dashed border-gray-300 rounded-xl">
                            {t(
                                'imagingreporting.no_images',
                                'No images are attached to this study. They may still be in the archive — nothing here proves they do not exist, only that this record has none.'
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2">
                            {images.map((img, index) => (
                                <div
                                    key={img.url || index}
                                    data-testid={`image-${index}`}
                                    className="relative group rounded-xl overflow-hidden border border-gray-200 bg-slate-900 aspect-square"
                                >
                                    {brokenImages.has(index) ? (
                                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 text-center">
                                            <ImageOff className="w-5 h-5 text-slate-400" />
                                            <span className="text-[10px] text-slate-300">
                                                {t('imagingreporting.image_expired', 'Link expired — refresh')}
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            <img
                                                src={img.url}
                                                alt={t('imagingreporting.image_alt', 'Image {{n}} of {{total}}', { n: index + 1, total: images.length })}
                                                loading="lazy"
                                                onError={() => setBrokenImages((s) => new Set(s).add(index))}
                                                onClick={() => setLightbox(img.url)}
                                                className="w-full h-full object-contain cursor-zoom-in"
                                            />
                                            <a
                                                href={img.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                title={t('imagingreporting.open_full', 'Open full size')}
                                                className="absolute top-1 right-1 p-1 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </a>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Objects the study RECORDS that produced no viewable link. The
                        gap is invisible otherwise — the grid simply has fewer tiles —
                        and "fewer pictures than were taken" is a fact a radiologist
                        has to know before deciding they have seen the series. */}
                    {imagesHeld > images.length && (
                        <div data-testid="images-unviewable" className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            {t(
                                'imagingreporting.images_unviewable',
                                '{{n}} image(s) recorded on this study could not be opened. They exist in storage; the link failed. Do not read this as a shorter series.',
                                { n: imagesHeld - images.length }
                            )}
                        </div>
                    )}

                    {/* ---- what the console recorded ---- */}
                    <div className="mt-5 p-3 rounded-xl border border-gray-200 bg-slate-50">
                        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                            {t('imagingreporting.acquisition', 'Acquisition')}
                        </h3>
                        <Fact label={t('imagingreporting.machine', 'Machine')} value={[equipment?.name, equipment?.room].filter(Boolean).join(' · ')} />
                        <Fact label={t('imagingreporting.performed', 'Performed')} value={fmtDateTime(study?.performedAt)} />
                        {/* The radiographer by NAME or not at all. An internal id in
                            the place a person's name belongs is the same defect the
                            PDF renderer refuses to commit. */}
                        <Fact label={t('imagingreporting.radiographer', 'Radiographer')} value={study?.performedByName} />
                        <Fact
                            label={t('imagingreporting.contrast', 'Contrast')}
                            value={study?.contrastUsed
                                ? (study.contrastAgent || t('imagingreporting.contrast_given', 'given'))
                                : (procedure?.requiresContrast ? t('imagingreporting.contrast_not_given', 'not given') : '')}
                        />
                        <Fact label={t('imagingreporting.dicom_uid', 'Study UID')} value={study?.dicomStudyUid} mono />
                        <Fact label={t('imagingreporting.radiation_dose', 'Dose')} value={procedure?.radiationDose} />
                        {order?.clinicalNotes && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                    {t('imagingreporting.referral_note', 'Referral note')}
                                </div>
                                <div className="text-[11px] text-slate-600 whitespace-pre-wrap">{order.clinicalNotes}</div>
                            </div>
                        )}
                        {study?.remarks && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                    {t('imagingreporting.console_remarks', 'Console remarks')}
                                </div>
                                <div className="text-[11px] text-slate-600 whitespace-pre-wrap">{study.remarks}</div>
                            </div>
                        )}
                        {study?.technique && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                    {t('imagingreporting.acquisition_technique', 'Technique recorded at the console')}
                                </div>
                                <div className="text-[11px] text-slate-600 whitespace-pre-wrap">{study.technique}</div>
                                {/* Copied on a CLICK and never by default. The report's
                                    technique is the radiologist's own statement of what
                                    was done, and the document deliberately does not fall
                                    back to the console's note (see pdfkitRenderer) — so
                                    adopting it has to be somebody's decision. */}
                                {editable && !trimmed(draftText.technique) && (
                                    <button
                                        type="button"
                                        data-testid="copy-technique"
                                        onClick={() => setField('technique', study.technique)}
                                        className="mt-1 text-[11px] text-[#2D8A8A] font-semibold underline underline-offset-2 cursor-pointer"
                                    >
                                        {t('imagingreporting.use_as_technique', 'Use this as the report’s technique')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ================================================================
                THE WALL.

                Two panes, side by side, and everything about them is different:
                background, weight, wording, and the fact that only one of them
                opens a dialog. Writing an interpretation and signing for it are not
                two sizes of the same action, and a reading room under pressure
                reads that difference from the shape of the controls long before it
                reads the labels on them.
               ================================================================ */}
            <div
                data-testid="reporting-action-bar"
                className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_20px_rgba(15,23,42,0.08)]"
            >
                <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-3 lg:items-stretch">
                    {/* ---- DRAFT: quiet, frequent, reversible ---- */}
                    <div className="flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50">
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                                {mode === 'amend'
                                    ? t('imagingreporting.amend_pane', 'Issue the amendment')
                                    : t('imagingreporting.draft_pane', 'Write the report')}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 truncate">
                                {mode === 'amend'
                                    ? (trimmed(amendReason).length < MIN_AMEND_REASON
                                        ? t('imagingreporting.amend_needs_reason', 'A reason is required — it prints on the amended report')
                                        : !changed
                                            ? t('imagingreporting.amend_needs_change', 'Nothing has been changed yet')
                                            : t('imagingreporting.amend_ready', 'Ready to issue as a new version'))
                                    : mode === 'locked'
                                        ? t('imagingreporting.locked_pane', 'Signed — the text is fixed. Amend to correct it.')
                                        : changed
                                            ? t('imagingreporting.unsaved', 'Typed but not saved')
                                            : t('imagingreporting.all_saved', 'Everything on screen is saved')}
                            </div>
                        </div>

                        {mode === 'amend' ? (
                            <button
                                type="button"
                                data-testid="issue-amendment"
                                disabled={!amendReady || saving}
                                onClick={handleIssueAmendment}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-amber-500 text-amber-700 font-semibold text-sm whitespace-nowrap ${(!amendReady || saving) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-amber-50'}`}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                                {t('imagingreporting.issue_amendment', 'Issue amendment')}
                            </button>
                        ) : canDraft ? (
                            <button
                                type="button"
                                data-testid="save-draft"
                                disabled={!editable || !changed || saving}
                                onClick={handleSaveDraft}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm whitespace-nowrap ${(!editable || !changed || saving) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-[#4ABDBD]/10'}`}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {t('imagingreporting.save_draft', 'Save draft')}
                            </button>
                        ) : (
                            <span
                                data-testid="cannot-draft-pane"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-400 text-xs font-semibold whitespace-nowrap"
                            >
                                <Lock className="w-4 h-4" />
                                {t('imagingreporting.read_only', 'You may read, not report')}
                            </span>
                        )}
                    </div>

                    {/* ---- SIGN: rare, deliberate, and a signature ---- */}
                    <div
                        data-testid="sign-pane"
                        className={`flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 ${signBlockedBy ? 'border-slate-200 bg-slate-100' : 'border-slate-900 bg-slate-900'}`}
                    >
                        <div className="min-w-0">
                            <div className={`text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 ${signBlockedBy ? 'text-slate-500' : 'text-[#4ABDBD]'}`}>
                                <ShieldCheck className="w-3.5 h-3.5" />
                                {t('imagingreporting.sign_pane', 'Radiologist’s signature')}
                            </div>

                            {/* Every closed state says what would open it, in the
                                order of who can do something about it. */}
                            {signBlockedBy === 'permission' && (
                                <div data-testid="sign-blocked-permission" className="text-xs text-slate-600 mt-0.5">
                                    {t(
                                        'imagingreporting.sign_no_permission',
                                        'You can write a report but not sign it. A registered radiologist holding radiology.report.sign has to put their name to it before it can be released.'
                                    )}
                                </div>
                            )}
                            {signBlockedBy === 'study' && (
                                <div data-testid="sign-blocked-study" className="text-xs text-slate-600 mt-0.5">
                                    {t(
                                        'imagingreporting.sign_blocked_study',
                                        'The images have not been acquired, so there is nothing to put a signature to.'
                                    )}
                                </div>
                            )}
                            {signBlockedBy === 'amending' && (
                                <div data-testid="sign-blocked-amending" className="text-xs text-amber-700 mt-0.5">
                                    {t(
                                        'imagingreporting.sign_blocked_amending',
                                        'Issue the amendment first. It becomes a new draft, and that draft is what gets signed.'
                                    )}
                                </div>
                            )}
                            {signBlockedBy === 'nothing' && (
                                <div data-testid="sign-blocked-nothing" className="text-xs text-slate-600 mt-0.5">
                                    {liveReport
                                        ? t('imagingreporting.sign_already', 'This report is already signed.')
                                        : t('imagingreporting.sign_nothing', 'There is no draft to sign yet.')}
                                </div>
                            )}
                            {signBlockedBy === 'unsaved' && (
                                <div data-testid="sign-blocked-unsaved" className="text-xs text-amber-700 mt-0.5">
                                    {t(
                                        'imagingreporting.sign_unsaved',
                                        'Save first. A signature is applied to what is stored, not to what is on screen.'
                                    )}
                                </div>
                            )}
                            {signBlockedBy === 'registration' && (
                                <div data-testid="sign-blocked-registration" className="text-xs text-amber-700 mt-0.5">
                                    {t(
                                        'imagingreporting.sign_no_registration',
                                        'Your record has no registration number. A report carries the number of whoever signed it, so signing is not possible until an administrator adds one.'
                                    )}
                                </div>
                            )}
                            {!signBlockedBy && (
                                <div className="text-xs text-slate-300 mt-0.5">
                                    {t('imagingreporting.sign_ready', 'Ready to sign {{no}}', { no: liveReport?.reportNo || '' })}
                                    <span className="text-slate-400">
                                        {signerIdentity.name ? ` · ${signerIdentity.name}` : ''}
                                        {signerIdentity.registrationNo
                                            ? ` · ${t('imagingreporting.reg_no', 'Reg. no')} ${signerIdentity.registrationNo}`
                                            : ''}
                                    </span>
                                </div>
                            )}
                        </div>

                        {signBlockedBy ? (
                            <span
                                data-testid="sign-locked"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-400 text-xs font-semibold whitespace-nowrap cursor-not-allowed"
                            >
                                <Lock className="w-4 h-4" />
                                {t('imagingreporting.sign', 'Sign')}
                            </span>
                        ) : (
                            <button
                                type="button"
                                data-testid="sign-report"
                                disabled={signing}
                                onClick={handleSign}
                                className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#4ABDBD] text-slate-900 font-black text-sm uppercase tracking-wide whitespace-nowrap shadow-lg ${signing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-[#5fd0d0]'}`}
                            >
                                {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
                                {t('imagingreporting.sign_and_name', 'Sign the report')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ---- one image, large ---- */}
            {lightbox && (
                <div
                    data-testid="image-lightbox"
                    role="presentation"
                    onClick={() => setLightbox(null)}
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
                >
                    <button
                        type="button"
                        onClick={() => setLightbox(null)}
                        aria-label={t('imagingreporting.close', 'Close')}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <img
                        src={lightbox}
                        alt={t('imagingreporting.image_large_alt', 'Study image')}
                        className="max-h-full max-w-full object-contain"
                    />
                </div>
            )}
        </div>
    );
}
